/**
 * Shared domain enums.
 *
 * Values are intentionally identical to the Flexv core domain (Go backend
 * `models` package) so the coach portal can switch to real data later without
 * a mapping layer.
 */

export const COACH_EMPLOYMENT_TYPES = ["in_house", "freelance"] as const;
export type CoachEmploymentType = (typeof COACH_EMPLOYMENT_TYPES)[number];

export const COACH_ASSIGNMENT_SOURCE_TYPES = [
    "manual",
    "class_schedule",
    "class_pass_schedule_occurrence",
    "venue_booking",
] as const;
export type CoachAssignmentSourceType = (typeof COACH_ASSIGNMENT_SOURCE_TYPES)[number];

export const COACH_ASSIGNMENT_ROLES = ["lead_coach", "assistant_coach", "support_staff"] as const;
export type CoachAssignmentRole = (typeof COACH_ASSIGNMENT_ROLES)[number];

export const COACH_ASSIGNMENT_STATUSES = [
    "scheduled",
    "confirmed",
    "ongoing",
    "completed",
    "cancelled",
    "no_show",
] as const;
export type CoachAssignmentStatus = (typeof COACH_ASSIGNMENT_STATUSES)[number];

export const COACH_PAYROLL_ELIGIBILITY = [
    "merchant_pays_coach",
    "external_no_payroll",
    "included_but_merchant_pays",
    "pending_payroll_decision",
] as const;
export type CoachPayrollEligibility = (typeof COACH_PAYROLL_ELIGIBILITY)[number];

/** Rentang agregasi dashboard coach. */
export const DASHBOARD_RANGES = ["week", "month"] as const;
export type DashboardRange = (typeof DASHBOARD_RANGES)[number];

/**
 * Tipe layanan pada `coach_commercial_prices.service_type` — sama persis
 * dengan validasi mobile API core.
 */
export const COMMERCIAL_SERVICE_TYPES = [
    "venue_booking",
    "venue_booking_add_on",
    "membership",
] as const;
export type CommercialServiceType = (typeof COMMERCIAL_SERVICE_TYPES)[number];

/** Status daftar harga; nilai lain (mis. `draft`) dibiarkan mentah. */
export const COMMERCIAL_PRICE_STATUSES = ["active", "inactive"] as const;
export type CommercialPriceStatus = (typeof COMMERCIAL_PRICE_STATUSES)[number];

export const COMMERCIAL_SERVICE_TYPE_LABELS: Record<CommercialServiceType, string> = {
    venue_booking: "Venue Booking",
    venue_booking_add_on: "Venue Booking Add-on",
    membership: "Membership",
};

/* -------------------------------------------------------------------------- */
/* Kepemilikan akun coach & jalur persetujuan afiliasi (migrasi 019)          */
/* -------------------------------------------------------------------------- */

/**
 * Kepemilikan AKUN coach — sumbu yang BERBEDA dari `employment_type`
 * (hubungan kerja). Dua kolom ini tidak saling menggantikan.
 *
 * - `none`             record operasional milik company/outlet, tanpa login.
 * - `self_registered`  coach mendaftar sendiri lewat Coach Portal.
 * - `merchant_managed` akun dibuatkan merchant (punya kredensial).
 * - `invited`          diundang merchant, belum diklaim / belum set password.
 */
export const COACH_ACCOUNT_TYPES = [
    "none",
    "self_registered",
    "merchant_managed",
    "invited",
] as const;
export type CoachAccountType = (typeof COACH_ACCOUNT_TYPES)[number];

/** Coach yang bisa login ke portal (punya akun sendiri). */
export const COACH_ACCOUNT_TYPES_WITH_LOGIN: readonly CoachAccountType[] = [
    "self_registered",
    "merchant_managed",
    "invited",
];

/**
 * Jalur persetujuan permintaan afiliasi — pihak yang mengajukan memilih salah
 * satu bila coach sudah punya akun dan sudah terafiliasi.
 */
export const AFFILIATION_APPROVAL_ROUTES = ["coach", "owner_company"] as const;
export type AffiliationApprovalRoute = (typeof AFFILIATION_APPROVAL_ROUTES)[number];

export const COACH_AFFILIATION_STATUSES = [
    "active",
    "pending_approval",
    "rejected",
    "inactive",
    "terminated",
] as const;
export type CoachAffiliationStatus = (typeof COACH_AFFILIATION_STATUSES)[number];

/** Account lifecycle on `coaches.status` (migration 017). */
export const COACH_STATUSES = ["pending_verification", "active", "suspended"] as const;
export type CoachStatus = (typeof COACH_STATUSES)[number];

/** Session lifecycle on `coach_sessions.status` (migration 017). */
export const COACH_SESSION_STATUSES = ["active", "failed", "expired", "revoked"] as const;
export type CoachSessionStatus = (typeof COACH_SESSION_STATUSES)[number];

/** OTP purpose on `coach_otp_challenges.purpose` (migration 017). */
export const COACH_OTP_PURPOSES = [
    "signup_email",
    "signup_phone",
    "login_phone",
    "reset_password",
    "change_email",
    "change_phone",
] as const;
export type CoachOtpPurpose = (typeof COACH_OTP_PURPOSES)[number];

export const COACH_OTP_CHANNELS = ["email", "phone"] as const;
export type CoachOtpChannel = (typeof COACH_OTP_CHANNELS)[number];

/** Monday-first, matching the portal week view. */
/** Hari dalam seminggu mengikuti `day_of_week` di DB: 1 = Monday .. 7 = Sunday. */
export const WEEK_DAYS = [
    { value: 1, label: "Mon", longLabel: "Monday" },
    { value: 2, label: "Tue", longLabel: "Tuesday" },
    { value: 3, label: "Wed", longLabel: "Wednesday" },
    { value: 4, label: "Thu", longLabel: "Thursday" },
    { value: 5, label: "Fri", longLabel: "Friday" },
    { value: 6, label: "Sat", longLabel: "Saturday" },
    { value: 7, label: "Sun", longLabel: "Sunday" },
] as const;

export const ASSIGNMENT_STATUS_LABELS: Record<CoachAssignmentStatus, string> = {
    scheduled: "Scheduled",
    confirmed: "Confirmed",
    ongoing: "Ongoing",
    completed: "Completed",
    cancelled: "Cancelled",
    no_show: "No Show",
};

export const ASSIGNMENT_ROLE_LABELS: Record<CoachAssignmentRole, string> = {
    lead_coach: "Lead Coach",
    assistant_coach: "Assistant Coach",
    support_staff: "Support Staff",
};

export const ASSIGNMENT_SOURCE_LABELS: Record<CoachAssignmentSourceType, string> = {
    manual: "Manual",
    class_schedule: "Class Schedule",
    class_pass_schedule_occurrence: "Class Pass Session",
    venue_booking: "Venue Booking",
};

export const EMPLOYMENT_TYPE_LABELS: Record<CoachEmploymentType, string> = {
    in_house: "In House",
    freelance: "Freelance",
};

export const PAYROLL_ELIGIBILITY_LABELS: Record<CoachPayrollEligibility, string> = {
    merchant_pays_coach: "Merchant Pays Coach",
    external_no_payroll: "External (No Payroll)",
    included_but_merchant_pays: "Included, Merchant Pays",
    pending_payroll_decision: "Pending Decision",
};
